import React from 'react';
import { css } from '@compiled/react';
import type { ApolloQueryResult } from 'apollo-client';
import type { ExecutionResult, MutationFunctionOptions } from 'react-apollo';

import { Flex, xcss } from '@atlaskit/primitives';
import Tag, { SimpleTag } from '@atlaskit/tag';

import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { LABELLED_CONTENT } from '@confluence/named-routes';

import type {
	DetailsPanelContentLabelsQuery,
	DetailsPanelContentLabelsQuery_singleContent_labels_nodes as LabelNode,
} from '../queries/__types__/DetailsPanelContentLabelsQuery';
import type {
	DetailsPanelContentLabelDeleteMutation,
	DetailsPanelContentLabelDeleteMutationVariables,
} from '../queries/__types__/DetailsPanelContentLabelDeleteMutation';

const labelsStyles = xcss({
	marginLeft: 'space.250',
});

type LabelProps = {
	label: string;
	hasEditPermissions: boolean;
	refetch: () => Promise<ApolloQueryResult<DetailsPanelContentLabelsQuery>>;
	handleDeleteLabelClick: (
		options?: MutationFunctionOptions<
			DetailsPanelContentLabelDeleteMutation,
			DetailsPanelContentLabelDeleteMutationVariables
		>,
	) => Promise<ExecutionResult<DetailsPanelContentLabelDeleteMutation>>;
};

const labelLinkStyles = css({
	textDecoration: 'none',
	color: 'inherit',
	'&:hover, &:visited, &:focus, &:active': {
		textDecoration: 'none',
		color: 'inherit',
	},
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const Label = ({ label, hasEditPermissions, refetch, handleDeleteLabelClick }: LabelProps) => {
	const [contentId = ''] = usePageContentId();
	const [spaceKey = ''] = usePageSpaceKey();

	const handleRemove = async () => {
		await handleDeleteLabelClick({
			variables: {
				contentId,
				label,
			},
		});

		await refetch();
	};

	if (fg('confluence_make_labels_clickable_content_wrapper')) {
		let labelUrl;
		try {
			labelUrl = LABELLED_CONTENT.toUrl({ spaceKey, labelName: encodeURIComponent(label) });
		} catch (e) {
			// There are many cases with special characters in labels; provide a graceful fallback in case
			// a URL for a label cannot be generated.
			labelUrl = '#';
		}
		return hasEditPermissions ? (
			<Tag
				key={label}
				text={label}
				onAfterRemoveAction={handleRemove}
				href={labelUrl}
				linkComponent={({ href, children }) => (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a href={href} css={labelLinkStyles}>
						{children}
					</a>
				)}
			/>
		) : (
			<SimpleTag
				testId="readonly-label"
				key={label}
				text={label}
				href={labelUrl}
				linkComponent={({ href, children }) => (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a href={href} css={labelLinkStyles}>
						{children}
					</a>
				)}
			/>
		);
	} else {
		return hasEditPermissions ? (
			<Tag key={label} text={label} onAfterRemoveAction={handleRemove} />
		) : (
			<SimpleTag testId="readonly-label" key={label} text={label} />
		);
	}
};

type LabelsProps = Omit<LabelProps, 'label'> & {
	labels: (LabelNode | null)[];
};

export const Labels = ({
	labels,
	refetch,
	hasEditPermissions,
	handleDeleteLabelClick,
}: LabelsProps) => (
	<Flex xcss={labelsStyles} direction="row" alignItems="center" wrap="wrap">
		{labels.map((labelNode) =>
			labelNode?.label ? (
				<Label
					refetch={refetch}
					key={labelNode.label}
					label={labelNode.label}
					hasEditPermissions={hasEditPermissions}
					handleDeleteLabelClick={handleDeleteLabelClick}
				/>
			) : null,
		)}
	</Flex>
);
